import React, { useEffect, useRef, useState } from 'react';
import { FaMicrophone, FaStop, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { BACKEND_MAIN_URL } from '../config';
import { Capacitor } from '@capacitor/core';
import WorkspaceDialog from './WorkspaceDialog';

/**
 * Mic button — records audio via MediaRecorder, POSTs it to /api/transcribe,
 * and calls onTranscribe(text) with the result so the parent can fill the input.
 *
 * States: idle → recording → transcribing → idle
 */
const MicButton = ({ onTranscribe, disabled, size = 18, title = 'Record voice note', answerTo }) => {
  const { t } = useTranslation('workspace');
  const [status, setStatus] = useState('idle'); // 'idle' | 'recording' | 'transcribing'
  const [notice, setNotice] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  // Stop any live stream when unmounting so we don't leave the mic open.
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        setNotice('unavailable');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        // Release the mic immediately
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size === 0) {
          setStatus('idle');
          return;
        }

        setStatus('transcribing');
        try {
          const form = new FormData();
          form.append('audio', blob, 'voice.webm');
          // A guided question being answered: the server hears the numbers with it as context.
          if (answerTo) form.append('answer_to', answerTo);
          const token = localStorage.getItem('token');
          const resp = await axios.post(`${BACKEND_MAIN_URL}/api/transcribe`, form, {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 60000,
          });
          const text = (resp?.data?.text || '').trim();
          if (text) onTranscribe(text);
        } catch (err) {
          console.error('Transcription failed:', err);
          setNotice('transcription');
        } finally {
          setStatus('idle');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus('recording');
    } catch (err) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      console.error('Microphone recording unavailable:', err);
      setNotice(['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(err?.name) ? 'permission' : 'unavailable');
      setStatus('idle');
    }
  };

  const openSettings = async () => {
    try {
      await Capacitor.Plugins.MicrophoneSettings.open();
      setNotice(null);
    } catch (err) {
      console.error('Could not open microphone settings:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const onClick = () => {
    if (status === 'idle') startRecording();
    else if (status === 'recording') stopRecording();
  };

  const isDisabled = disabled || status === 'transcribing';
  const label =
    status === 'recording' ? t('recording')
      : status === 'transcribing' ? t('transcribing')
      : title;

  return <>
    <button
      type="button"
      className={`mic-btn${status === 'recording' ? ' is-recording' : ''}`}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={label}
      title={label}
      style={{
        background: status === 'recording' ? '#ef4444' : 'transparent',
        color: status === 'recording' ? '#fff' : 'inherit',
        border: '1px solid #ddd',
        borderRadius: '50%',
        width: 36,
        height: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s ease',
      }}
    >
      {status === 'transcribing' ? (
        <FaSpinner size={size} style={{ animation: 'spin 1s linear infinite' }} />
      ) : status === 'recording' ? (
        <FaStop size={size} />
      ) : (
        <FaMicrophone size={size} />
      )}
    </button>
    {notice && <WorkspaceDialog title={t(notice === 'permission' ? 'microphonePermissionTitle' : 'microphoneUnavailableTitle')} onClose={() => setNotice(null)}>
      <div className="ws-microphone-help">
        <p>{t(notice === 'permission' ? 'microphonePermissionHelp' : notice === 'transcription' ? 'microphoneTranscriptionFailed' : 'microphoneUnavailable')}</p>
        <div className="ws-dialog-actions">
          <button type="button" className="ws-secondary" onClick={() => setNotice(null)}>{t('close')}</button>
          {notice === 'permission' && Capacitor.isNativePlatform() && <button type="button" className="ws-primary" onClick={openSettings}>{t('microphoneOpenSettings')}</button>}
        </div>
      </div>
    </WorkspaceDialog>}
  </>;
};

export default MicButton;
